import { apiClient } from '@/api/client';
import { Business, getStaticCategory, getStaticFilter } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { CheckboxGroup } from '@/components/ui/CheckboxGroup';
import { FormErrorMessage } from '@/components/ui/FormErrorMessage';
import { TabNavigation } from '@/components/ui/TabNavigation';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { useAuth } from '@/lib/auth';
import { queryKeys } from '@/lib/queryKeys';
import { BusinessFormData } from '@/pages/businesses/hooks/useBusinessMutations';
import { mdiCheck, mdiClose, mdiStore } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const STATUS_OPTIONS = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

interface BusinessFormProps {
  business?: Business | null;
  onSubmit: (data: BusinessFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const getFormValues = (business?: Business | null): BusinessFormData => ({
  name: business?.name || '',
  address: business?.address || '',
  postalCode: business?.postalCode || '',
  city: business?.city || '',
  province: business?.province || '',
  country: business?.country || '',
  website: business?.website || '',
  phone: business?.phone || '',
  status: business?.status || 'Pending',
  categoryIds: business?.businessCategories?.map(bc => bc.category?.id!) || [],
  filterIds: business?.filters?.map(bf => bf.filter?.id!) || [],
  openingHours:
    business?.openingHours?.map(oh => ({
      dayOfWeek: oh.dayOfWeek!,
      openTime: oh.openTime?.substring(0, 5) || '09:00',
      closeTime: oh.closeTime?.substring(0, 5) || '17:00',
    })) || [],
});

export const BusinessForm: React.FC<BusinessFormProps> = ({
  business,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = React.useState<
    'basic' | 'location' | 'hours' | 'categories'
  >('basic');

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    reset,
    setValue,
  } = useForm<BusinessFormData>({
    defaultValues: getFormValues(business),
    mode: 'onChange',
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: [queryKeys.ADMIN_CATEGORIES],
    queryFn: async () => {
      const response = await apiClient.GET('/category');
      if (response.error) throw new Error('Failed to fetch categories');
      return response.data || [];
    },
    enabled: isAuthenticated,
  });

  // Fetch filters
  const { data: filters } = useQuery({
    queryKey: [queryKeys.ADMIN_FILTERS],
    queryFn: async () => {
      const response = await apiClient.GET('/filter');
      if (response.error) throw new Error('Failed to fetch filters');
      return response.data || [];
    },
    enabled: isAuthenticated,
  });

  React.useEffect(() => {
    if (business) {
      reset(getFormValues(business));
    }
  }, [business, reset]);

  const openingHours = watch('openingHours');
  const selectedCategoryIds = watch('categoryIds');
  const selectedFilterIds = watch('filterIds');

  const handleFormSubmit = async (data: BusinessFormData) => {
    await onSubmit(data);
  };

  const toggleDay = (dayIndex: number) => {
    const existingHour = openingHours.find(oh => oh.dayOfWeek === dayIndex);
    if (existingHour) {
      setValue(
        'openingHours',
        openingHours.filter(oh => oh.dayOfWeek !== dayIndex),
        { shouldDirty: true },
      );
    } else {
      setValue(
        'openingHours',
        [
          ...openingHours,
          { dayOfWeek: dayIndex, openTime: '09:00', closeTime: '17:00' },
        ],
        { shouldDirty: true },
      );
    }
  };

  const updateHourTime = (
    dayIndex: number,
    field: 'openTime' | 'closeTime',
    value: string,
  ) => {
    setValue(
      'openingHours',
      openingHours.map(oh =>
        oh.dayOfWeek === dayIndex ? { ...oh, [field]: value } : oh,
      ),
      { shouldDirty: true },
    );
  };

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='p-6 border-b border-gray-200'>
        <div className='flex items-center space-x-3'>
          <div className='w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center'>
            <svg
              viewBox='0 0 24 24'
              fill='currentColor'
              className='w-6 h-6 text-blue-600'
            >
              <path d={mdiStore} />
            </svg>
          </div>
          <div>
            <h3 className='text-lg font-semibold text-gray-900'>
              Edit Business
            </h3>
            <p className='text-sm text-gray-600'>
              Update business information and settings
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <TabNavigation
        tabs={[
          { id: 'basic', label: 'Basic Info' },
          { id: 'location', label: 'Location' },
          { id: 'hours', label: 'Hours' },
          { id: 'categories', label: 'Categories & Filters' },
        ]}
        activeTab={activeTab}
        onTabChange={tab =>
          setActiveTab(tab as 'basic' | 'location' | 'hours' | 'categories')
        }
        size='sm'
      />

      {/* Form Content */}
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className='flex-1 overflow-y-auto p-6'
      >
        {activeTab === 'basic' && (
          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Business Name *
              </label>
              <Controller
                name='name'
                control={control}
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='text'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder='Enter business name'
                  />
                )}
              />
              <FormErrorMessage message={errors.name?.message} />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Status *
              </label>
              <Controller
                name='status'
                control={control}
                rules={{ required: 'Status is required' }}
                render={({ field }) => (
                  <select
                    {...field}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Website
              </label>
              <Controller
                name='website'
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type='url'
                    className='w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='https://example.com'
                  />
                )}
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Phone
              </label>
              <Controller
                name='phone'
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type='tel'
                    className='w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='+1 (555) 123-4567'
                  />
                )}
              />
            </div>
          </div>
        )}

        {activeTab === 'location' && (
          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Address *
              </label>
              <Controller
                name='address'
                control={control}
                rules={{ required: 'Address is required' }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='text'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.address ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder='123 Main St'
                  />
                )}
              />
              <FormErrorMessage message={errors.address?.message} />
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  City *
                </label>
                <Controller
                  name='city'
                  control={control}
                  rules={{ required: 'City is required' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type='text'
                      className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.city ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder='City'
                    />
                  )}
                />
                <FormErrorMessage message={errors.city?.message} />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Province/State
                </label>
                <Controller
                  name='province'
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type='text'
                      className='w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      placeholder='Province'
                    />
                  )}
                />
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Postal Code *
                </label>
                <Controller
                  name='postalCode'
                  control={control}
                  rules={{ required: 'Postal code is required' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type='text'
                      className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.postalCode ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder='12345'
                    />
                  )}
                />
                <FormErrorMessage message={errors.postalCode?.message} />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Country *
                </label>
                <Controller
                  name='country'
                  control={control}
                  rules={{ required: 'Country is required' }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type='text'
                      className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.country ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder='Country'
                    />
                  )}
                />
                <FormErrorMessage message={errors.country?.message} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hours' && (
          <div className='space-y-4'>
            <p className='text-sm text-gray-600 mb-4'>
              Select days and set opening/closing times for each day.
            </p>
            {DAY_NAMES.map((day, dayIndex) => {
              const hours = openingHours.find(oh => oh.dayOfWeek === dayIndex);
              const isOpen = !!hours;
              return (
                <div
                  key={dayIndex}
                  className={`p-3 rounded-lg border ${isOpen ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                >
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center space-x-3'>
                      <ToggleSwitch
                        value={isOpen}
                        onChange={() => toggleDay(dayIndex)}
                      />
                      <span className='font-medium text-gray-900'>{day}</span>
                    </div>
                    {isOpen && (
                      <div className='flex items-center space-x-2'>
                        <input
                          type='time'
                          value={hours?.openTime || '09:00'}
                          onChange={e =>
                            updateHourTime(dayIndex, 'openTime', e.target.value)
                          }
                          className='px-2 py-1 border border-gray-300 rounded-md text-sm'
                        />
                        <span className='text-gray-500'>to</span>
                        <input
                          type='time'
                          value={hours?.closeTime || '17:00'}
                          onChange={e =>
                            updateHourTime(
                              dayIndex,
                              'closeTime',
                              e.target.value,
                            )
                          }
                          className='px-2 py-1 border border-gray-300 rounded-md text-sm'
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'categories' && (
          <div className='space-y-6'>
            <div>
              <h4 className='text-sm font-medium text-gray-700 mb-3'>
                Categories
              </h4>
              <CheckboxGroup
                options={
                  categories?.map(c => {
                    const staticCategory = getStaticCategory(c.name || '');
                    return {
                      value: c.id!,
                      label: `${staticCategory?.emoji || '📁'} ${staticCategory?.label || c.name}`,
                    };
                  }) || []
                }
                selectedValues={selectedCategoryIds}
                onChange={ids =>
                  setValue('categoryIds', ids, { shouldDirty: true })
                }
                color='purple'
              />
            </div>

            <div>
              <h4 className='text-sm font-medium text-gray-700 mb-3'>
                Filters
              </h4>
              <CheckboxGroup
                options={
                  filters?.map(f => {
                    const staticFilter = getStaticFilter(f.name || '');
                    return {
                      value: f.id!,
                      label: `${staticFilter?.emoji || '🏷️'} ${staticFilter?.label || f.name}`,
                    };
                  }) || []
                }
                selectedValues={selectedFilterIds}
                onChange={ids =>
                  setValue('filterIds', ids, { shouldDirty: true })
                }
                color='blue'
              />
            </div>
          </div>
        )}
      </form>

      {/* Action Buttons */}
      <div className='p-6 border-t border-gray-200'>
        <div className='flex items-center justify-end space-x-3'>
          <Button
            type='button'
            label='Cancel'
            icon={mdiClose}
            color='light'
            onClick={onCancel}
            disabled={loading}
          />
          <Button
            type='button'
            label='Update Business'
            icon={mdiCheck}
            color='primary'
            loading={loading}
            disabled={!isValid || !isDirty}
            onClick={handleSubmit(handleFormSubmit)}
          />
        </div>
      </div>
    </div>
  );
};
